import React from "react";
import { Mail, Instagram, Twitter, Youtube, ChevronRight } from "lucide-react";
import TikTok from "@/components/icons/TikTok";
import Card, { CardBody } from "@/components/ui/Card";
import { ArrowLink } from "@/components/ui/CTAButton";

// Tarjeta de miembro del equipo.
//
// El componente exportaba tres variantes —`card`, `compact` y `horizontal`—
// pero solo se usaba la primera: Quiénes Somos la renderiza sin `variant` y la
// ficha individual se dibujaba su propia mini-tarjeta a mano. Las otras dos
// eran código muerto y se han retirado.
//
// Los colores pasan a los tokens del sistema (`primary`, `muted-foreground`)
// en lugar de `text-gray-900` y `text-blue-600` sueltos, que era lo que hacía
// que esta tarjeta desentonara con el resto de la web.
const MiembroCard = ({ miembro, showBio = false, className = "" }) => {
  if (!miembro) return null;

  const redes = [
    miembro.redes?.instagram && { href: miembro.redes.instagram, icon: Instagram, label: "Instagram" },
    miembro.redes?.twitter && { href: miembro.redes.twitter, icon: Twitter, label: "Twitter / X" },
    miembro.redes?.youtube && { href: miembro.redes.youtube, icon: Youtube, label: "YouTube" },
    miembro.redes?.tiktok && { href: miembro.redes.tiktok, icon: TikTok, label: "TikTok" },
  ].filter(Boolean);

  return (
    <div className={className}>
      <Card className="flex flex-col h-full" interactive>
        <div className="team-photo-frame relative">
          {miembro.foto ? (
            <img
              src={miembro.foto}
              alt={`Foto de ${miembro.nombre}`}
              loading="lazy"
              decoding="async"
              onError={e => { e.target.onerror = null; e.target.src = "/logos/logo-icono.png"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-5xl text-primary/20 font-bold select-none" aria-hidden="true">
                {miembro.nombre.charAt(0)}
              </span>
            </div>
          )}
          {miembro.area && (
            <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs font-semibold px-2.5 py-1 rounded-full">
              {miembro.area}
            </span>
          )}
        </div>

        <CardBody className="p-5 flex flex-col flex-1">
          <h3 className="text-lg font-heading font-bold leading-snug">{miembro.nombre}</h3>
          <p className="text-sm font-semibold text-primary mt-0.5 mb-3">{miembro.cargo}</p>
          <p className="text-sm text-muted-foreground leading-relaxed flex-1">
            {showBio ? miembro.bio : miembro.descripcion}
          </p>

          <div className="mt-4 flex items-center gap-3 border-t border-border pt-4">
            {miembro.email && (
              <a
                href={`mailto:${miembro.email}`}
                aria-label={`Escribir a ${miembro.nombre}`}
                className="text-muted-foreground hover:text-primary transition-colors duration-fast focus-ring rounded"
              >
                <Mail size={18} aria-hidden="true" />
              </a>
            )}
            {redes.map(red => (
              <a
                key={red.label}
                href={red.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${red.label} de ${miembro.nombre}`}
                className="text-muted-foreground hover:text-primary transition-colors duration-fast focus-ring rounded"
              >
                <red.icon size={18} aria-hidden="true" />
              </a>
            ))}
            <ArrowLink to={`/quienes-somos/equipo/${miembro.slug}`} className="ml-auto font-semibold">
              Ver perfil <ChevronRight size={16} aria-hidden="true" />
            </ArrowLink>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default MiembroCard;
